import React, { useRef, useEffect, useState } from "react";
import playerjs from "@gumlet/player.js";

// Add browser compatibility check
const isBrowserCompatible = () => {
  const ua = navigator.userAgent;
  const isIE = ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
  const isEdge = ua.indexOf("Edge/") > -1;
  const isOldEdge =
    ua.indexOf("Edge/") > -1 && parseInt(ua.split("Edge/")[1]) < 79;

  // Check for modern browser features
  const hasModernFeatures =
    typeof window.Promise !== "undefined" &&
    typeof window.fetch !== "undefined" &&
    typeof window.URL !== "undefined" &&
    typeof window.URLSearchParams !== "undefined";

  return !isIE && !isOldEdge && hasModernFeatures;
};

export function GumletPlayer({
  videoID,
  title = "Gumlet video player",
  style = { padding: "56.25% 0 0 0", position: "relative" },
  schemaOrgVideoObject = {},
  version = "",
  playerRef,
  disablePlayerControls = true,
  autoplay = true,
  loop = true,
  ...props
}) {
  const iframeRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const [isCompatible, setIsCompatible] = useState(true);
  const Player = playerjs?.Player;

  // Store event callbacks in refs to avoid stale closures
  const propsRef = useRef(props);
  propsRef.current = props;

  useEffect(() => {
    // Check browser compatibility
    setIsCompatible(isBrowserCompatible());

    if (!isCompatible) {
      console.warn(
        "Your browser may not fully support the video player. Please update to a modern browser."
      );
      return;
    }

    if (!iframeRef.current || !Player) return;
    if (playerInstanceRef.current) return; // Guard against double initialization

    const instance = new Player(iframeRef.current);
    playerInstanceRef.current = instance;
    if (playerRef) playerRef.current = instance;

    // Set up events with error handling
    function capitalize(e) {
      return e.charAt(0).toUpperCase() + e.slice(1);
    }

    const events = [
      "ready",
      "pause",
      "play",
      "progress",
      "timeupdate",
      "ended",
      "fullscreenChange",
      "pipChange",
      "audioChange",
      "qualityChange",
      "volumeChange",
      "seeked",
      "error",
      "playbackRateChange",
    ];

    for (const event of events) {
      try {
        instance.on(event, (e) => propsRef.current[`on${capitalize(event)}`]?.(e));
      } catch (error) {
        console.warn(`Failed to set up ${event} event handler:`, error);
      }
    }

    // Cleanup
    return () => {
      try {
        if (iframeRef.current) {
          iframeRef.current.src = "about:blank";
        }
      } catch (error) {
        console.warn("Error cleaning up player:", error);
      }
      playerInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!videoID) return <div>Error: videoID is required</div>;
  if (!isCompatible) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-yellow-800">
          Your browser may not fully support the video player. Please update to
          a modern browser like Chrome, Firefox, Safari, or Edge.
        </p>
      </div>
    );
  }

  const srcURL = new URL(
    `https://play${version ? `-${version}` : ""
    }.gumlet.io/embed/${videoID}?disable_player_controls=${disablePlayerControls}&autoplay=${autoplay}&loop=${loop}&playsinline=1`
  );

  try {
    for (const [key, value] of Object.entries(props)) {
      srcURL.searchParams.append(key, value);
    }
  } catch (error) {
    console.warn("Error setting URL parameters:", error);
  }

  return (
    <div style={{ ...style, position: "relative" }}>
      {JSON.stringify(schemaOrgVideoObject) !== "{}" && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schemaOrgVideoObject),
          }}
        />
      )}
      <iframe
        ref={iframeRef}
        loading="eager"
        title={title}
        src={srcURL.toString()}
        style={{
          border: "none",
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
        }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen; playsinline;"
      />
    </div>
  );
}
